/*
 * MTO Design Upload — Multi-file upload block
 * Uploads to Google Drive via Apps Script.
 * Speed: single FileReader per file, all uploads run in parallel.
 * Features: animated progress bar, abort support.
 */

if (!customElements.get('mto-design-upload')) {
  class MtoDesignUpload extends HTMLElement {
    connectedCallback() {
      this.uploadUrl  = this.dataset.uploadUrl  || '';
      this.maxSizeMb  = parseInt(this.dataset.maxSizeMb)  || 50;
      this.maxFiles   = parseInt(this.dataset.maxFiles)   || 3;
      this.isRequired = this.dataset.required === 'true';
      this.formId     = this.dataset.formId;
      this.propName   = this.dataset.propertyName || 'Tasarım Dosyası';
      /* Ürün handle'ı: sunucu dosyayı '<tarih>_<urun>_<ad>' olarak
         adlandırmak için kullanır. Referer'a güvenilemez — tarayıcı
         cross-origin isteklerde yol kısmını kırpar. */
      this.productHandle = this.dataset.productHandle || '';

      this.inputEl  = this.querySelector('.design-upload__input');
      this.dropzone = this.querySelector('.design-upload__dropzone');
      this.filesEl  = this.querySelector('.design-upload__files');
      this.statusEl = this.querySelector('.design-upload__status');

      this._files  = [];
      this._nextId = 0;
      this._defaultCta = this.querySelector('.design-upload__cta')?.textContent || '';

      this._bindEvents();
      this._bindFormHook();
    }

    /* ── Events ──────────────────────────────────────────────── */

    _bindEvents() {
      /* Use event delegation on dropzone so it works even after input replacement */
      this.dropzone.addEventListener('change', (e) => {
        if (!e.target.matches('.design-upload__input')) return;
        Array.from(e.target.files).forEach(f => this._queue(f));
        /* Replace the input entirely — most reliable cross-browser reset */
        this._resetFileInput();
      });

      this.dropzone.addEventListener('dragover', (e) => {
        e.preventDefault();
        this.dropzone.classList.add('is-dragover');
      });
      this.dropzone.addEventListener('dragleave', () => {
        this.dropzone.classList.remove('is-dragover');
      });
      this.dropzone.addEventListener('drop', (e) => {
        e.preventDefault();
        this.dropzone.classList.remove('is-dragover');
        Array.from(e.dataTransfer.files).forEach(f => this._queue(f));
      });
    }

    _resetFileInput() {
      const oldInput = this.inputEl;
      const newInput = oldInput.cloneNode(false);
      oldInput.parentNode.replaceChild(newInput, oldInput);
      this.inputEl = newInput;
    }

    _bindFormHook() {
      const form = document.getElementById(this.formId);
      if (!form) return;

      form.addEventListener('submit', (e) => {
        const uploading = this._files.filter(f => !f.url && !f.error);
        const done      = this._files.filter(f =>  f.url);

        if (uploading.length > 0) {
          e.preventDefault(); e.stopImmediatePropagation();
          this._setStatus('error', 'Dosyalar yükleniyor, lütfen bekleyin.');
          this.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }

        if (done.length === 0 && this.isRequired) {
          e.preventDefault(); e.stopImmediatePropagation();
          this._setStatus('error', 'Lütfen en az bir tasarım dosyası yükleyin.');
          this.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }

        form.querySelectorAll('[data-mto-design]').forEach(el => el.remove());
        done.forEach((f, i) => {
          const suffix = done.length > 1 ? ` ${i + 1}` : '';
          const inp = document.createElement('input');
          inp.type = 'hidden';
          inp.name = `properties[${this.propName}${suffix}]`;
          inp.value = f.url;
          inp.dataset.mtoDesign = '1';
          form.appendChild(inp);
        });
      }, true);
    }

    /* ── File handling ───────────────────────────────────────── */

    _queue(file) {
      if (this._files.length >= this.maxFiles) return;

      const maxBytes = this.maxSizeMb * 1024 * 1024;
      if (file.size > maxBytes) {
        this._setStatus('error', `"${file.name}" — en fazla ${this.maxSizeMb} MB olabilir.`);
        return;
      }

      /* Clear previous success status when starting a new upload */
      this._clearStatus();

      const entry = { id: this._nextId++, name: file.name, type: file.type, size: file.size, url: null, error: false, _aborted: false };
      this._files.push(entry);
      const row = this._addRow(entry);
      this._updateUI();

      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target.result;

        if (file.type.startsWith('image/')) {
          const img = document.createElement('img');
          img.src = dataUrl;
          img.alt = '';
          img.className = 'design-upload__file-img';
          const icon = row.querySelector('.design-upload__file-icon');
          if (icon) icon.replaceWith(img);
        }

        this._upload(dataUrl, entry, row);
      };
      reader.onerror = () => {
        entry.error = true;
        this._finishProgress(row, false);
        this._setStatus('error', `"${file.name}" okunamadı.`);
      };
      reader.readAsDataURL(file);
    }

    _addRow(entry) {
      const row = document.createElement('div');
      row.className = 'design-upload__file-row';
      row.dataset.fileId = entry.id;

      const icon = document.createElement('span');
      icon.className = 'design-upload__file-icon';
      icon.textContent = '\uD83D\uDCC4';
      row.appendChild(icon);

      const info = document.createElement('div');
      info.className = 'design-upload__file-info';

      const name = document.createElement('span');
      name.className = 'design-upload__file-name';
      name.textContent = entry.name;
      info.appendChild(name);

      const progressWrap = document.createElement('div');
      progressWrap.className = 'design-upload__progress';
      const progressBar = document.createElement('div');
      progressBar.className = 'design-upload__progress-bar';
      progressBar.style.width = '3%';
      progressWrap.appendChild(progressBar);
      info.appendChild(progressWrap);

      row.appendChild(info);

      const st = document.createElement('span');
      st.className = 'design-upload__file-st design-upload__file-st--uploading';
      st.textContent = '...';
      row.appendChild(st);

      const rm = document.createElement('button');
      rm.type = 'button';
      rm.className = 'design-upload__file-rm';
      rm.setAttribute('aria-label', 'Kaldır');
      rm.innerHTML = '&times;';
      rm.addEventListener('click', () => this._remove(entry.id));
      row.appendChild(rm);

      this.filesEl.appendChild(row);
      this.filesEl.removeAttribute('hidden');
      return row;
    }

    _remove(id) {
      const entry = this._files.find(f => f.id === id);
      if (entry) {
        entry._aborted = true;
        if (entry._abort) entry._abort.abort();
      }
      this._files = this._files.filter(f => f.id !== id);
      this.filesEl.querySelector(`[data-file-id="${id}"]`)?.remove();
      if (this._files.length === 0) this.filesEl.setAttribute('hidden', '');
      this._updateUI();
      this._clearStatus();
    }

    _updateUI() {
      const atMax = this._files.length >= this.maxFiles;
      this.dropzone.hidden = atMax;

      const cta = this.querySelector('.design-upload__cta');
      if (cta) {
        cta.textContent = this._files.length > 0
          ? `Başka dosya ekle (${this._files.length}/${this.maxFiles})`
          : this._defaultCta;
      }

      this.dropzone.classList.toggle('has-files', this._files.length > 0);
    }

    /* ── Animated progress simulation ────────────────────────── */

    _startProgress(entry, row) {
      const bar = row.querySelector('.design-upload__progress-bar');
      const st = row.querySelector('.design-upload__file-st');
      if (!bar) return null;

      /* Start with visible progress immediately */
      bar.style.width = '5%';
      if (st) st.textContent = '5%';

      const sizeMb = entry.size / (1024 * 1024);
      const estimatedMs = (2000 + sizeMb * 800);
      const targetPct = 90;
      const startTime = Date.now();

      const timer = setInterval(() => {
        if (entry._aborted) { clearInterval(timer); return; }
        const elapsed = Date.now() - startTime;
        const progress = 1 - Math.exp(-3 * elapsed / estimatedMs);
        const current = Math.max(5, Math.min(Math.round(progress * targetPct), targetPct));
        bar.style.width = current + '%';
        if (st) st.textContent = current + '%';
      }, 80);

      return timer;
    }

    _finishProgress(row, success) {
      const bar = row.querySelector('.design-upload__progress-bar');
      const progressWrap = row.querySelector('.design-upload__progress');
      const st = row.querySelector('.design-upload__file-st');

      if (bar) bar.style.width = '100%';

      if (success) {
        if (progressWrap) progressWrap.classList.add('design-upload__progress--done');
        if (st) {
          st.textContent = '\u2713';
          st.className = 'design-upload__file-st design-upload__file-st--done';
        }
      } else {
        if (progressWrap) progressWrap.classList.add('design-upload__progress--error');
        if (st) {
          st.textContent = '!';
          st.className = 'design-upload__file-st design-upload__file-st--error';
        }
      }
    }

    /* ── Upload ──────────────────────────────────────────────── */

    async _upload(dataUrl, entry, row) {
      /* No endpoint — store filename only */
      if (!this.uploadUrl) {
        entry.url = entry.name;
        this._finishProgress(row, true);
        this._checkAllDone();
        return;
      }

      const abortController = new AbortController();
      entry._abort = abortController;

      const progressTimer = this._startProgress(entry, row);

      try {
        const res = await fetch(this.uploadUrl, {
          method: 'POST',
          body: JSON.stringify({ name: entry.name, file: dataUrl, handle: this.productHandle }),
          signal: abortController.signal,
          redirect: 'follow',
          cache: 'no-store'
        });

        if (progressTimer) clearInterval(progressTimer);
        if (entry._aborted) return;

        /* Google Apps Script redirects; after following we should get JSON */
        const text = await res.text();
        if (entry._aborted) return;

        let data;
        try {
          data = JSON.parse(text);
        } catch (_) {
          console.error('[MTO Design Upload] Non-JSON response:', text.substring(0, 200));
          throw new Error('Sunucu geçersiz yanıt verdi. Apps Script URL kontrol edin.');
        }

        if (!data.url) throw new Error(data.error || 'URL alınamadı');

        entry.url = data.url;
        this._finishProgress(row, true);
        this._checkAllDone();
      } catch (err) {
        if (progressTimer) clearInterval(progressTimer);
        if (entry._aborted || err.name === 'AbortError') return;

        console.error('[MTO Design Upload]', err);
        entry.error = true;
        this._finishProgress(row, false);
        this._setStatus('error', `"${entry.name}" yüklenemedi. ${err.message || ''} × ile kaldırıp tekrar deneyin.`);
      }
    }

    _checkAllDone() {
      const pending = this._files.filter(f => !f.url && !f.error);
      if (pending.length === 0) {
        const done = this._files.filter(f => f.url);
        if (done.length > 0) this._setStatus('success', `${done.length} dosya yüklendi \u2713`);
      }
    }

    /* ── Helpers ─────────────────────────────────────────────── */

    _setStatus(type, msg) {
      this.statusEl.className = `design-upload__status design-upload__status--${type}`;
      this.statusEl.textContent = msg;
      this.statusEl.removeAttribute('hidden');
    }

    _clearStatus() {
      this.statusEl.setAttribute('hidden', '');
    }
  }

  customElements.define('mto-design-upload', MtoDesignUpload);
}
